import { Navigate, type RouteObject, createBrowserRouter } from "react-router";

import { AppLayout } from "@/routes/AppLayout";
import { NotFound } from "@/routes/NotFound";

/**
 * Las rutas, traducidas una a una desde el App Router de Next.
 *
 * ## El orden importa en tres lugares
 *
 * `agents/assignment`, `segments/new` y `campaigns/new` van declaradas **antes**
 * que sus hermanas dinámicas. React Router rankea los segmentos estáticos por
 * encima de los dinámicos y las resolvería bien igual, pero se declaran
 * explícitas porque el modo en que esto falla es silencioso: si alguna vez el
 * ranking no aplica, `/segments/new` se parsea como `segmentId="new"` y la
 * pantalla pide el segmento cero. No hay error, solo una pantalla vacía.
 *
 * ## El fallback de historial
 *
 * Toda ruta desconocida sirve `index.html`. En desarrollo lo hace Vite; en
 * producción hay que configurarlo en el CDN. Sin eso, entrar directo a
 * `/inbox/123` —o volver de Google a `/calendar/connected`— da 404 antes de que
 * la aplicación llegue a cargar.
 *
 * ## `/calendar/connected`
 *
 * Es el `redirect_uri` registrado en Google. No se puede renombrar sin volver a
 * darlo de alta en la consola de Google, y hay once usuarios ya conectados.
 */
export const routes: RouteObject[] = [
  {
    path: "/login",
    lazy: async () => ({ Component: (await import("@/features/auth/LoginPage")).LoginPage }),
  },
  {
    path: "/",
    Component: AppLayout,
    children: [
      /* Las pantallas rediseñadas ponen su propio padding (las de alto completo,
       * ninguno); el resto sigue con el `p-6` del shell. */
      {
        index: true,
        handle: { ownsPadding: true },
        lazy: async () => ({ Component: (await import("@/features/home/HomePage")).HomePage }),
      },

      /* La bandeja es una ruta con hijas: el shell —filtros y lista— se monta
       * una sola vez y el panel derecho cambia con `<Outlet/>`. En Next estaba
       * duplicado en las dos páginas porque los layouts no reciben
       * `searchParams`; acá esa limitación no existe. */
      {
        path: "inbox",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/inbox/InboxShell")).InboxShell,
        }),
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import("@/features/inbox/InboxEmpty")).InboxEmpty,
            }),
          },
          {
            path: ":conversationId",
            lazy: async () => ({
              Component: (await import("@/features/inbox/ConversationView")).ConversationView,
            }),
          },
        ],
      },

      {
        path: "pipeline",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/pipeline/PipelinePage")).PipelinePage,
        }),
      },
      {
        path: "contacts",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/contacts/ContactsPage")).ContactsPage,
        }),
      },
      {
        path: "contacts/:contactId",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/contacts/ContactsPage")).ContactDetailPage,
        }),
      },

      // Estática antes que dinámica. Ver la nota de arriba.
      {
        path: "agents/assignment",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/assignment/AssignmentPage")).AssignmentPage,
        }),
      },
      {
        path: "agents",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/agents/AgentsPage")).AgentsPage,
        }),
      },
      // Crear un agente: el selector y sus dos caminos. Estáticas antes que
      // `agents/:agentId`, por lo mismo que `agents/assignment`.
      {
        path: "agents/new",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/agents/create/CreateAgentSelector"))
            .CreateAgentSelector,
        }),
      },
      {
        path: "agents/new/wizard",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/agents/create/AgentWizard")).AgentWizard,
        }),
      },
      {
        path: "agents/new/chat",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/agents/create/ChatBuilderPreview"))
            .ChatBuilderPreview,
        }),
      },
      {
        path: "agents/:agentId",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/agents/AgentPage")).AgentPage,
        }),
      },

      {
        path: "segments/new",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/segments/SegmentsPage")).NewSegmentPage,
        }),
      },
      {
        path: "segments",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/segments/SegmentsPage")).SegmentsPage,
        }),
      },
      {
        path: "segments/:segmentId",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/segments/SegmentsPage")).EditSegmentPage,
        }),
      },

      {
        path: "campaigns/new",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/campaigns/CampaignsPage")).NewCampaignPage,
        }),
      },
      {
        path: "campaigns",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/campaigns/CampaignsPage")).CampaignsPage,
        }),
      },
      {
        path: "campaigns/:campaignId",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/campaigns/CampaignsPage")).CampaignPage,
        }),
      },

      {
        path: "automations",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/automations/AutomationsPage")).AutomationsPage,
        }),
      },
      {
        path: "analytics",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/analytics/AnalyticsPage")).AnalyticsPage,
        }),
      },
      {
        path: "calendar/connected",
        lazy: async () => ({
          Component: (await import("@/features/calendar/CalendarPage")).CalendarConnectedPage,
        }),
      },
      {
        path: "calendar",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/calendar/CalendarPage")).CalendarPage,
        }),
      },
      {
        path: "integrations",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/channels/IntegrationsPage")).IntegrationsPage,
        }),
      },
      /* Configuración es una sola pantalla con navegación propia: el marco se
       * monta una vez y cada sección es una ruta hija. `/settings` a secas abre
       * la primera. */
      {
        path: "settings",
        handle: { ownsPadding: true },
        lazy: async () => ({
          Component: (await import("@/features/settings/SettingsPage")).SettingsLayout,
        }),
        children: [
          { index: true, element: <Navigate to="pipeline" replace /> },
          {
            path: "pipeline",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage"))
                .PipelineSettingsPage,
            }),
          },
          {
            path: "sources",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage"))
                .LeadSourcesSettingsPage,
            }),
          },
          {
            path: "email",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage"))
                .InboundEmailSettingsPage,
            }),
          },
          {
            path: "intent",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage")).IntentSettingsPage,
            }),
          },
          {
            path: "handoff",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage")).HandoffSettingsPage,
            }),
          },
          {
            path: "reminders",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage"))
                .ReminderSettingsPage,
            }),
          },
          {
            path: "dashboard",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage"))
                .DashboardSettingsPage,
            }),
          },
          {
            path: "menu",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage")).MenuSettingsPage,
            }),
          },
          {
            path: "theme",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage")).ThemeSettingsPage,
            }),
          },
          {
            path: "industry",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage"))
                .IndustrySettingsPage,
            }),
          },
          {
            path: "usage",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage")).UsageReportPage,
            }),
          },
          {
            path: "claude",
            lazy: async () => ({
              Component: (await import("@/features/settings/SettingsPage")).McpTokensPage,
            }),
          },
        ],
      },
    ],
  },
  { path: "*", Component: NotFound },
];

/**
 * Las rutas van exportadas aparte del router para que los tests puedan montar
 * las mismas definiciones sobre un router de memoria. Probar contra una copia
 * de las rutas no probaría nada: el bug que buscamos es justamente que las
 * definiciones reales queden en el orden equivocado.
 */
export const router = createBrowserRouter(routes);
